import React, { useEffect, useState } from 'react';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import { BatteryWarning, MessageSquareX, Coffee, CheckCircle2, XCircle, BookOpen, Headphones, MessageCircle, Zap, ShieldCheck, Lock, X } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Accordion } from '../components/ui/Accordion';
import { initMetaPixel, trackPageView } from '../utils/metaPixel';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/qnB2gvbssiZM9RPoQwNS4D/El_protocolo_de_una_mujer_proveedora.png';
const PRIVACY_CONTENT = `Política de Privacidad

Última actualización: Marzo 2026

1. Información que Recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito directamente; todos los pagos son procesados por proveedores de pago seguros y certificados.

2. Uso de la Información
Tu información personal se utiliza exclusivamente para:
• Procesar y entregar tu compra digital.
• Enviarte el acceso al material adquirido.
• Comunicarnos contigo respecto a tu pedido.

3. Protección de Datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. No Compartimos tu Información
No vendemos, intercambiamos ni transferimos tu información personal a terceros. La única excepción son los proveedores de servicios que nos asisten en la operación de nuestro sitio y el procesamiento de pagos, quienes están obligados a mantener la confidencialidad.

5. Cookies
Nuestro sitio puede utilizar cookies para mejorar la experiencia del usuario y con fines analíticos. Puedes desactivar las cookies en la configuración de tu navegador.

6. Tus Derechos
Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, contáctanos a través de los canales disponibles.

7. Cambios en esta Política
Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página.`;
const TERMS_CONTENT = `Términos y Condiciones

Última actualización: Marzo 2026

1. Aceptación de los Términos
Al realizar tu compra, aceptas estos términos y condiciones en su totalidad. Si no estás de acuerdo con alguno de estos términos, te pedimos que no realices la compra.

2. Producto Digital
"El Protocolo de la Mujer Proveedora" es un producto digital de contenido educativo e informativo. El acceso se otorga de forma inmediata tras confirmar el pago.

3. Uso Personal
El contenido adquirido es para uso personal y exclusivo del comprador. Queda prohibida la reproducción, distribución, modificación o reventa del material sin autorización expresa.

4. Naturaleza del Contenido
Este producto ofrece información y herramientas de desarrollo personal. No constituye asesoramiento médico, psicológico ni terapéutico profesional. Si te encuentras en una situación de abuso o peligro, busca ayuda profesional especializada.

5. Política de Reembolso
Debido a la naturaleza digital del producto y su entrega inmediata, no se ofrecen reembolsos una vez que se ha otorgado el acceso al material.

6. Limitación de Responsabilidad
Los resultados pueden variar según cada persona. No garantizamos resultados específicos. El uso del material y la aplicación de las técnicas es responsabilidad exclusiva del comprador.

7. Propiedad Intelectual
Todo el contenido, incluyendo textos, audios, gráficos y metodologías, está protegido por derechos de autor. Todos los derechos están reservados.

8. Modificaciones
Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor desde su publicación.`;
function LegalModal({
  isOpen,
  onClose,
  title,
  content
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}) {
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-13">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-navy-dark/80 backdrop-blur-sm" onClick={onClose} data-id="element-14">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="bg-cream rounded-sm max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-15">
            <div className="flex items-center justify-between p-6 border-b border-gold/20" data-id="element-16">
              <h3 className="font-serif text-2xl text-navy" data-id="element-17">{title}</h3>
              <button onClick={onClose} className="text-navy/50 hover:text-navy transition-colors p-1" data-id="element-18">
                <X size={24} data-id="element-19" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-20">
              {content.split('\n').map((line, i) => <p key={i} className={`text-navy/80 leading-relaxed ${line.trim() === '' ? 'h-4' : ''} ${line.startsWith('Política') || line.startsWith('Términos') ? 'hidden' : ''} ${/^\d+\./.test(line.trim()) ? 'font-bold text-navy mt-6 mb-2' : 'mb-1'}`} data-id="element-21">
                  {line}
                </p>)}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-22">
    {children}
  </motion.div>;
export function LandingPage() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [legalModal, setLegalModal] = useState<'privacy' | 'terms' | null>(null);
  const {
    scrollY
  } = useScroll();
  useEffect(() => {
    initMetaPixel();
    trackPageView();
  }, []);
  useMotionValueEvent(scrollY, 'change', latest => {
    if (latest > 600) {
      setShowStickyCTA(true);
    } else {
      setShowStickyCTA(false);
    }
  });
  const scrollToOffer = () => {
    document.getElementById('offer-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-cream selection:bg-gold/30 selection:text-navy-dark overflow-x-hidden" data-id="element-23">
      {/* 1. HERO SECTION */}
      <section className="relative bg-navy text-cream pt-20 pb-24 md:pt-32 md:pb-32 px-6 overflow-hidden" data-id="element-24">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-25">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-gold/5 blur-[120px]" data-id="element-26" />
          <div className="absolute bottom-[-10%] left-[-10%] w-[400px] h-[400px] rounded-full bg-navy-light/20 blur-[100px]" data-id="element-27" />
        </div>

        <div className="max-w-5xl mx-auto relative z-10 flex flex-col items-center text-center" data-id="element-28">
          <FadeIn data-id="element-29">
            <span className="text-gold tracking-[0.2em] uppercase text-xs md:text-sm font-bold mb-6 block" data-id="element-30">
              Para la mujer que lo resuelve todo
            </span>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-tight mb-8" data-id="element-31">
              EL PROTOCOLO DE LA <br className="hidden md:block" data-id="element-32" />
              <span className="text-gold italic" data-id="element-33">MUJER PROVEEDORA</span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-34">
            <p className="text-lg md:text-xl text-cream/90 max-w-3xl mx-auto leading-relaxed mb-12 font-light" data-id="element-35">
              Cómo apagar el "modo jefa" al llegar a casa, soltar el control y
              volver a ser la mujer deseada y cuidada{' '}
              <span className="font-semibold text-gold" data-id="element-36">
                (sin sacrificar tu éxito profesional).
              </span>
            </p>
          </FadeIn>

          <FadeIn delay={0.4} className="w-full max-w-md mx-auto mb-12" data-id="element-37">
            <div className="relative" data-id="element-38">
              <div className="absolute inset-0 bg-gold/20 blur-2xl rounded-full transform scale-90" data-id="element-39" />
              <img src={PRODUCT_IMAGE} alt="El Protocolo de la Mujer Proveedora" className="relative z-10 w-full h-auto drop-shadow-2xl hover:scale-105 transition-transform duration-700" data-id="element-40" />
            </div>
          </FadeIn>

          <FadeIn delay={0.6} className="w-full max-w-md flex flex-col items-center" data-id="element-41">
            <Button onClick={scrollToOffer} fullWidth className="mb-4 text-sm md:text-base" data-id="element-42">
              Quiero recuperar mi paz y mi magnetismo
            </Button>
            <div className="flex items-center gap-2 text-cream/70 text-sm" data-id="element-43">
              <ShieldCheck size={16} className="text-gold" data-id="element-44" />
              <span data-id="element-45">Acceso inmediato</span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 2. OPENING HOOK */}
      <section className="py-24 px-6 bg-cream relative" data-id="element-46">
        <div className="max-w-3xl mx-auto text-center" data-id="element-47">
          <FadeIn data-id="element-48">
            <h2 className="font-serif text-3xl md:text-4xl text-navy mb-8 leading-tight" data-id="element-49">
              Eres imparable en la oficina. Lideras, resuelves, decides y
              provees.
            </h2>
            <div className="w-16 h-[1px] bg-gold mx-auto mb-8" data-id="element-50" />
            <p className="text-lg md:text-xl text-navy/80 leading-relaxed mb-6" data-id="element-51">
              Pero al cerrar la laptop, sientes que no puedes "apagar" ese
              motor. Llegas a casa y sigues gestionando a tu pareja como si
              fuera un empleado más.
            </p>
            <p className="text-lg md:text-xl text-navy/80 leading-relaxed font-medium" data-id="element-52">
              El resultado es un agotamiento crónico, un resentimiento que crece
              cada noche y una intimidad que se siente como un pendiente más en
              tu lista de tareas.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 3. THE PROBLEM */}
      <section className="py-24 px-6 bg-navy text-cream relative border-y border-gold/10" data-id="element-53">
        <div className="max-w-4xl mx-auto" data-id="element-54">
          <FadeIn data-id="element-55">
            <h2 className="font-serif text-3xl md:text-5xl text-center mb-16" data-id="element-56">
              El mundo te enseñó a ser fuerte para sobrevivir y ganar dinero.{' '}
              <span className="text-gold italic" data-id="element-57">Y lo lograste.</span>
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-58">
            <FadeIn delay={0.2} data-id="element-59">
              <p className="text-lg text-cream/80 leading-relaxed mb-6" data-id="element-60">
                Pero esa misma energía analítica, rígida y de mando que te hace
                exitosa en los negocios, está "secando" tu relación de pareja.
              </p>
              <ul className="space-y-6" data-id="element-61">
                {['Te sientes sola cargando con todas las decisiones del hogar.', 'Sientes que si tú no haces que las cosas pasen, nada ocurre.', "Te has convertido en la 'madre' o la 'jefa' de tu relación.", 'El deseo ha desaparecido bajo el peso de las responsabilidades.'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-62">
                    <div className="mt-1 bg-gold/20 p-1 rounded-full text-gold flex-shrink-0" data-id="element-63">
                      <XCircle size={18} data-id="element-64" />
                    </div>
                    <span className="text-cream/90" data-id="element-65">{item}</span>
                  </li>)}
              </ul>
            </FadeIn>

            <FadeIn delay={0.4} className="bg-navy-light/50 p-8 md:p-10 rounded-sm border border-gold/20 relative" data-id="element-66">
              <div className="absolute -top-4 -left-4 text-gold opacity-50" data-id="element-67">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" data-id="element-68">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" data-id="element-69" />
                </svg>
              </div>
              <p className="font-serif text-2xl md:text-3xl text-gold leading-tight text-center italic" data-id="element-70">
                "El problema no es tu pareja, es tu rigidez energética."
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 4. WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-24 px-6 bg-cream" data-id="element-71">
        <div className="max-w-5xl mx-auto" data-id="element-72">
          <FadeIn data-id="element-73">
            <div className="text-center mb-16" data-id="element-74">
              <span className="text-gold-dark tracking-widest uppercase text-sm font-bold mb-4 block" data-id="element-75">
                La Trampa del Control
              </span>
              <h2 className="font-serif text-3xl md:text-4xl text-navy" data-id="element-76">
                Lo que ya intentaste (y por qué no funcionó)
              </h2>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-77">
            {[{
            icon: <BatteryWarning size={32} data-id="element-78" />,
            title: "La 'Huelga de Brazos Caídos'",
            desc: 'Dejaste de hacer cosas para ver si él las hacía, pero solo aumentó tu ansiedad al ver el caos.'
          }, {
            icon: <MessageSquareX size={32} data-id="element-79" />,
            title: "Pedir 'Más Iniciativa'",
            desc: 'Lo hiciste desde la crítica y el reproche, lo que solo causó que él se alejara o se defendiera.'
          }, {
            icon: <Coffee size={32} data-id="element-80" />,
            title: 'Escapadas de Spa',
            desc: 'Te relajaste por una hora, pero regresaste a la misma dinámica de control al minuto de entrar por la puerta.'
          }].map((item, i) => <FadeIn key={i} delay={0.2 * (i + 1)} className="bg-white p-8 rounded-sm shadow-sm border border-gold/10 text-center hover:shadow-md transition-shadow" data-id="element-81">
                <div className="w-16 h-16 mx-auto bg-cream rounded-full flex items-center justify-center text-gold-dark mb-6" data-id="element-82">
                  {item.icon}
                </div>
                <h3 className="font-serif text-xl text-navy mb-4" data-id="element-83">
                  {item.title}
                </h3>
                <p className="text-navy/70 leading-relaxed" data-id="element-84">{item.desc}</p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 5. THE UNIQUE MECHANISM */}
      <section className="py-24 px-6 bg-navy text-cream relative overflow-hidden" data-id="element-85">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(201,169,110,0.1)_0%,transparent_70%)]" data-id="element-86" />
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-87">
          <FadeIn data-id="element-88">
            <Zap className="text-gold w-12 h-12 mx-auto mb-8" data-id="element-89" />
            <h2 className="font-serif text-3xl md:text-5xl mb-8 leading-tight" data-id="element-90">
              La solución no es trabajar menos, es el{' '}
              <br className="hidden md:block" data-id="element-91" />
              <span className="text-gold" data-id="element-92">"Interruptor de Polaridad"</span>
            </h2>
            <div className="w-24 h-[1px] bg-gold/50 mx-auto mb-8" data-id="element-93" />
            <p className="text-lg md:text-xl text-cream/90 leading-relaxed max-w-3xl mx-auto font-light" data-id="element-94">
              Es un sistema de descompresión neuro-emocional que te permite
              transitar de la energía masculina (ejecución) a la energía
              femenina (recepción) en menos de 15 minutos.
              <br data-id="element-95" />
              <br data-id="element-96" />
              Aprenderás a crear el "vacío" necesario para que tu pareja dé un
              paso al frente y asuma su rol de protección, mientras tú
              finalmente descansas en tu esencia.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 6. WHAT YOU GET */}
      <section className="py-24 px-6 bg-cream" data-id="element-97">
        <div className="max-w-5xl mx-auto" data-id="element-98">
          <FadeIn data-id="element-99">
            <h2 className="font-serif text-3xl md:text-4xl text-navy text-center mb-16" data-id="element-100">
              Lo que recibes hoy
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-101">
            <FadeIn delay={0.2} className="order-2 md:order-1" data-id="element-102">
              <div className="space-y-8" data-id="element-103">
                {[{
                icon: <BookOpen size={24} data-id="element-104" />,
                title: 'El Protocolo Maestro (Libro Digital)',
                desc: 'La hoja de ruta paso a paso para dejar de ser la proveedora emocional de tu relación.'
              }, {
                icon: <Zap size={24} data-id="element-105" />,
                title: 'El Método S.T.O.P.',
                desc: 'Técnica de transición rápida para no llevar el estrés del trabajo al comedor.'
              }, {
                icon: <MessageCircle size={24} data-id="element-106" />,
                title: 'Scripts de Comunicación Magnética',
                desc: 'Palabras exactas para pedir ayuda sin sonar como un sargento.'
              }, {
                icon: <Headphones size={24} data-id="element-107" />,
                title: 'Audio Masterclass de Regalo',
                desc: '"Meditación de Conexión Sensorial" para reprogramar tu cuerpo antes de ver a tu pareja.'
              }].map((item, i) => <div key={i} className="flex gap-6" data-id="element-108">
                    <div className="flex-shrink-0 w-12 h-12 bg-navy rounded-full flex items-center justify-center text-gold" data-id="element-109">
                      {item.icon}
                    </div>
                    <div data-id="element-110">
                      <h3 className="font-serif text-xl text-navy mb-2" data-id="element-111">
                        {item.title}
                      </h3>
                      <p className="text-navy/70 leading-relaxed" data-id="element-112">
                        {item.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </FadeIn>

            <FadeIn delay={0.4} className="order-1 md:order-2 flex justify-center" data-id="element-113">
              <img src={PRODUCT_IMAGE} alt="Contenido del Protocolo" className="w-full max-w-md drop-shadow-xl" data-id="element-114" />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 7. WHO THIS IS FOR / NOT FOR */}
      <section className="py-24 px-6 bg-white border-y border-gold/10" data-id="element-115">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-116">
          <FadeIn data-id="element-117">
            <div className="bg-cream/50 p-8 md:p-10 rounded-sm h-full border border-gold/20" data-id="element-118">
              <h3 className="font-serif text-2xl text-navy mb-8 flex items-center gap-3" data-id="element-119">
                <CheckCircle2 className="text-gold" data-id="element-120" /> Para ti si...
              </h3>
              <ul className="space-y-6" data-id="element-121">
                {["Dueñas de negocio, gerentes y profesionales que 'mandan' todo el día.", 'Mujeres que se sienten exhaustas de tomar todas las decisiones.', 'Mujeres que aman a su pareja pero sienten que la pasión se apagó por el estrés.', 'Líderes que quieren permitirse ser cuidadas sin sentirse débiles.'].map((item, i) => <li key={i} className="flex items-start gap-3 text-navy/80" data-id="element-122">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" data-id="element-123" />
                    <span data-id="element-124">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-125">
            <div className="bg-navy/5 p-8 md:p-10 rounded-sm h-full border border-navy/10" data-id="element-126">
              <h3 className="font-serif text-2xl text-navy mb-8 flex items-center gap-3 opacity-80" data-id="element-127">
                <XCircle className="text-navy/50" data-id="element-128" /> No es para ti si...
              </h3>
              <ul className="space-y-6" data-id="element-129">
                {['Mujeres que no tienen ambición profesional o metas claras.', 'Personas que buscan soluciones mágicas sin cambiar su comunicación.', 'Relaciones con dinámicas de abuso o peligro (esto requiere ayuda profesional).'].map((item, i) => <li key={i} className="flex items-start gap-3 text-navy/60" data-id="element-130">
                    <div className="w-1.5 h-1.5 rounded-full bg-navy/30 mt-2 flex-shrink-0" data-id="element-131" />
                    <span data-id="element-132">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 8. HOW IT WORKS */}
      <section className="py-24 px-6 bg-cream" data-id="element-133">
        <div className="max-w-5xl mx-auto" data-id="element-134">
          <FadeIn data-id="element-135">
            <h2 className="font-serif text-3xl md:text-4xl text-navy text-center mb-16" data-id="element-136">
              Cómo funciona
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-137">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-[1px] bg-gold/30" data-id="element-138" />

            {[{
            step: '1',
            title: 'Identifica',
            desc: 'Detectas los momentos donde tu energía de mando está bloqueando el amor.'
          }, {
            step: '2',
            title: 'Transita',
            desc: 'Aplicas el Interruptor de Polaridad al terminar tu jornada laboral.'
          }, {
            step: '3',
            title: 'Recibe',
            desc: 'Usas los scripts de comunicación para permitir que él lidere mientras tú recuperas tu paz.'
          }].map((item, i) => <FadeIn key={i} delay={0.2 * (i + 1)} className="relative z-10 text-center" data-id="element-139">
                <div className="w-24 h-24 mx-auto bg-navy rounded-full flex items-center justify-center text-gold font-serif text-3xl mb-6 shadow-lg border-4 border-cream" data-id="element-140">
                  {item.step}
                </div>
                <h3 className="font-serif text-2xl text-navy mb-4" data-id="element-141">
                  {item.title}
                </h3>
                <p className="text-navy/70 leading-relaxed" data-id="element-142">{item.desc}</p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 9. PROOF / BELIEVABILITY */}
      <section className="py-24 px-6 bg-navy text-cream text-center" data-id="element-143">
        <div className="max-w-3xl mx-auto" data-id="element-144">
          <FadeIn data-id="element-145">
            <div className="text-gold mb-8 flex justify-center gap-2" data-id="element-146">
              {[1, 2, 3, 4, 5].map(i => <svg key={i} className="w-6 h-6 fill-current" viewBox="0 0 24 24" data-id="element-147">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" data-id="element-148" />
                </svg>)}
            </div>
            <p className="font-serif text-2xl md:text-3xl leading-relaxed italic text-cream/90" data-id="element-149">
              "En solo 7 días, pasarás de sentirte una gestora de crisis
              doméstica a experimentar lo que es ser una mujer provista. Notarás
              cómo tu mandíbula se relaja, cómo el tono de voz de tu pareja
              cambia y cómo, por primera vez en años, te sientes segura soltando
              el mando."
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="py-24 px-6 bg-cream" data-id="element-150">
        <div className="max-w-3xl mx-auto" data-id="element-151">
          <FadeIn data-id="element-152">
            <h2 className="font-serif text-3xl md:text-4xl text-navy text-center mb-12" data-id="element-153">
              Preguntas Frecuentes
            </h2>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-154">
            <Accordion items={[{
            question: '¿Tengo que dejar de ser exitosa?',
            answer: 'No. Al contrario, este protocolo protege tu éxito al evitar que te quemes emocionalmente en casa. Aprenderás a compartimentar tu energía.'
          }, {
            question: '¿Y si mi pareja es muy pasiva?',
            answer: "Él es pasivo porque tú ocupas todo el espacio. Este protocolo te enseña a crear el lugar y el 'vacío' necesario para que él actúe y lidere."
          }, {
            question: '¿Cuánto tiempo toma ver resultados?',
            answer: 'Es inmediato. Aplicar el Método S.T.O.P. cambia tu química hormonal y tu lenguaje corporal desde la primera noche.'
          }, {
            question: '¿Es una suscripción?',
            answer: 'No, es un pago único de $6.39 USD que te da acceso inmediato y de por vida a todo el material.'
          }]} data-id="element-155" />
          </FadeIn>
        </div>
      </section>

      {/* 11. FINAL OFFER + CTA */}
      <section id="offer-section" className="py-24 px-6 bg-navy text-cream relative overflow-hidden" data-id="element-156">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay" data-id="element-157" />

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-158">
          <FadeIn data-id="element-159">
            <h2 className="font-serif text-4xl md:text-5xl mb-6" data-id="element-160">
              Recupera tu balance y tu magnetismo{' '}
              <span className="text-gold italic" data-id="element-161">hoy mismo.</span>
            </h2>
            <p className="text-xl text-cream/80 mb-12" data-id="element-162">
              Todo el sistema "Protocolo de la Mujer Proveedora" + Audio
              Masterclass.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} className="bg-navy-dark/50 p-8 md:p-12 rounded-sm border border-gold/30 max-w-2xl mx-auto backdrop-blur-sm" data-id="element-163">
            <img src={PRODUCT_IMAGE} alt="El Protocolo de la Mujer Proveedora" className="w-48 md:w-64 mx-auto mb-8 drop-shadow-2xl" data-id="element-164" />

            <div className="text-center mb-8" data-id="element-165">
              <span className="text-cream/60 line-through text-lg mr-3" data-id="element-166">
                $27 USD
              </span>
              <span className="text-gold font-serif text-5xl font-bold" data-id="element-167">
                $6.39 USD
              </span>
            </div>

            <Button fullWidth href="https://formacioninteligente.com/cart/44287296995463:1" className="text-sm md:text-lg py-5 mb-6" data-id="element-168">
              Quiero recuperar mi paz y mi magnetismo
            </Button>

            <div className="flex flex-col items-center gap-3 text-sm text-cream/60" data-id="element-169">
              <div className="flex items-center gap-2" data-id="element-170">
                <Lock size={16} data-id="element-171" /> Pago 100% seguro y encriptado
              </div>
              <div className="flex items-center gap-2" data-id="element-172">
                <Zap size={16} data-id="element-173" /> Acceso inmediato tras la compra
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-navy-dark py-10 px-6 border-t border-gold/10" data-id="element-174">
        <div className="max-w-4xl mx-auto text-center" data-id="element-175">
          <p className="font-serif text-lg text-cream/80 mb-4" data-id="element-176">
            El Protocolo de la Mujer Proveedora
          </p>
          <div className="w-12 h-[1px] bg-gold/30 mx-auto mb-4" data-id="element-177" />
          <div className="flex justify-center gap-6 mb-6" data-id="element-178">
            <button onClick={() => setLegalModal('privacy')} className="text-cream/50 hover:text-gold text-sm transition-colors underline underline-offset-4 decoration-cream/20 hover:decoration-gold" data-id="element-179">
              Política de Privacidad
            </button>
            <button onClick={() => setLegalModal('terms')} className="text-cream/50 hover:text-gold text-sm transition-colors underline underline-offset-4 decoration-cream/20 hover:decoration-gold" data-id="element-180">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-cream/30 text-xs" data-id="element-181">
            © {new Date().getFullYear()} Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* LEGAL MODALS */}
      <LegalModal isOpen={legalModal === 'privacy'} onClose={() => setLegalModal(null)} title="Política de Privacidad" content={PRIVACY_CONTENT} data-id="element-182" />
      <LegalModal isOpen={legalModal === 'terms'} onClose={() => setLegalModal(null)} title="Términos y Condiciones" content={TERMS_CONTENT} data-id="element-183" />

      {/* STICKY MOBILE CTA */}
      <motion.div initial={{
      y: 100
    }} animate={{
      y: showStickyCTA ? 0 : 100
    }} transition={{
      duration: 0.3
    }} className="fixed bottom-0 left-0 right-0 p-4 bg-navy/95 backdrop-blur-md z-50 md:hidden border-t border-gold/20" data-id="element-184">
        <Button onClick={scrollToOffer} fullWidth className="py-3 text-xs" data-id="element-185">
          QUIERO RECUPERAR MI PAZ ($6.39)
        </Button>
      </motion.div>
    </div>;
}